export const STORAGE_LOCATIONS = [
  'Fridge',
  'Freezer', 
  'Pantry',
  'Dry Storage',
  'Cold Room',
  'Walk-in Cooler',
  'Shelf',
  'Counter',
  'Backroom',
  'Cupboard',
  'Other'
];

export const toBaseUnit = (size, unit) => {
  if (size == null || unit == null) return null;
  const u = String(unit).trim().toLowerCase();
  const s = parseFloat(size);
  if (isNaN(s)) return null;

  if (['g', 'gram', 'grams'].includes(u)) return { value: s, baseUnit: 'g' };
  if (['kg', 'kilogram', 'kilograms'].includes(u)) return { value: s * 1000, baseUnit: 'g' };
  if (['mg', 'milligram', 'milligrams'].includes(u)) return { value: s / 1000, baseUnit: 'g' };
  if (['l', 'liter', 'litre', 'liters', 'litres'].includes(u)) return { value: s * 1000, baseUnit: 'ml' };
  if (['ml', 'milliliter', 'millilitre', 'milliliters', 'millilitres'].includes(u)) return { value: s, baseUnit: 'ml' };
  if (['oz', 'ounce', 'ounces'].includes(u)) return { value: s * 28.3495, baseUnit: 'g' };
  if (['lb', 'pound', 'lbs', 'pounds'].includes(u)) return { value: s * 453.592, baseUnit: 'g' };
  return { value: s, baseUnit: u };
};

export const parseCompactUnit = (text) => {
  if (!text) return null;
  const m = String(text).trim().match(/^(\d+(\.\d+)?)(\s*)([a-zA-Z]+)$/);
  if (!m) return null;
  return { size: parseFloat(m[1]), unit: m[4] };
};

/**
 * Calculate unit cost from package cost and package size
 * @param {number} packageCost - Cost of the package
 * @param {number} packageSize - Size of the package  
 * @returns {number} Unit cost per unit
 */
export const calculateUnitCost = (packageCost, packageSize) => {
  const cost = parseFloat(packageCost || 0);
  const size = parseFloat(packageSize || 1);
  return size > 0 ? cost / size : 0;
};

/**
 * Format cost information display for ingredient rows
 * @param {object} ingredient - Ingredient object
 * @returns {string} Formatted cost string
 */
export const formatCostInfo = (ingredient) => {
  const packageSize = ingredient.package_size;
  const packageUnit = ingredient.package_uom || ingredient.unit_of_measure;
  const packageCost = ingredient.package_cost;

  if (packageSize && packageUnit && packageCost) {
    const unitCost = calculateUnitCost(packageCost, packageSize);
    return `${packageSize} ${packageUnit} = $${parseFloat(packageCost).toFixed(2)} · 1 ${packageUnit} = $${unitCost.toFixed(6)}`;
  }

  // Fallback for legacy data
  if (ingredient.cost_per_unit != null) {
    return `$${parseFloat(ingredient.cost_per_unit).toFixed(4)} / ${ingredient.unit_of_measure || 'unit'}`;
  }
  
  return '-';
};

/**
 * Convert between compatible units
 * @param {number} value - Value to convert
 * @param {string} fromUnit - Source unit
 * @param {string} toUnit - Target unit
 * @returns {number} Converted value
 */
export const convertUnits = (value, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return value;
  
  const from = toBaseUnit(value, fromUnit);
  const to = toBaseUnit(1, toUnit);
  
  if (!from || !to || from.baseUnit !== to.baseUnit) {
    return value; // Cannot convert
  }
  
  return from.value / to.value;
};

/**
 * Get display string for expiry status
 * @param {object} ingredient - Ingredient with expiry dates
 * @returns {object} Status object with color and text
 */
export const getExpiryStatus = (ingredient) => {
  const today = new Date();
  const purchaseExpiry = ingredient.expiry_date_on_purchase ? new Date(ingredient.expiry_date_on_purchase) : null;
  const openedExpiry = ingredient.expiry_date_after_opened ? new Date(ingredient.expiry_date_after_opened) : null;
  
  const isExpired = (date) => date && date < today;
  const isExpiringSoon = (date) => {
    if (!date) return false;
    const daysToExpiry = (date - today) / (1000 * 60 * 60 * 24);
    return daysToExpiry <= 7 && daysToExpiry >= 0;
  };

  if (isExpired(purchaseExpiry) || isExpired(openedExpiry)) {
    return { status: 'expired', color: 'error', text: 'Expired' };
  }
  if (isExpiringSoon(purchaseExpiry) || isExpiringSoon(openedExpiry)) {
    return { status: 'expiring', color: 'warning', text: 'Expiring Soon' };
  }
  return { status: 'good', color: 'success', text: 'Good' };
};


/**
 * Calculate stock value
 * @param {number} currentStock - Current stock quantity
 * @param {number} unitCost - Cost per unit
 * @returns {number} Stock value
 */
export const calculateStockValue = (currentStock, unitCost) => {
  return parseFloat(currentStock || 0) * parseFloat(unitCost || 0);
};

/**
 * Get stock status based on current vs min stock
 * @param {number} currentStock - Current stock
 * @param {number} minStock - Minimum stock threshold
 * @returns {string} Stock status
 */
export const getStockStatus = (currentStock, minStock) => {
  const current = parseFloat(currentStock || 0);
  const min = parseFloat(minStock || 0);
  
  if (current === 0) return 'out_of_stock';
  if (current <= min) return 'low_stock';
  return 'normal';
};

/**
 * Format quantity with proper decimals
 * @param {number} quantity - Quantity to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted quantity
 */
export const formatQuantity = (quantity, decimals = 3) => {
  return parseFloat(quantity || 0).toFixed(decimals);
};
